import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild, ElementRef, ChangeDetectionStrategy, SimpleChanges, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GenericIdbaService } from '../../services/generic-idba-service.service';
import { SHARED_DIRECTIVES } from '../../utils/shared-directives';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { debounceTime, distinctUntilChanged, from, map, Observable, of, startWith, Subject, Subscription, switchMap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-generic-autocomplete',
  templateUrl: './generic-autocomplete.component.html',
  styleUrls: ['./generic-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule,
    FlexLayoutModule,
    MatIconModule,
    MatButtonModule,
    SHARED_DIRECTIVES,
    ReactiveFormsModule,
    FormsModule,
    MatAutocompleteModule,
    MatInputModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => GenericAutocompleteComponent),
    }
  ]
})

export class GenericAutocompleteComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() public service: GenericIdbaService;
  @Input() public fieldLabel: string;
  @Input() public formGroup;
  @Input() public formControlName: string;
  @Input() public autoLoad: boolean;
  @Input() public placeholder: string;
  @Input() public isCampoGenerico: boolean;
  @Input() public modulo: string;
  @Input() public assunto: string;
  @Input() public assuntos: string[] | null = null;
  @Input() public onSelectOptionAction: any;
  @Input() public originalClass: any;
  @Input() public readonly: boolean = false;
  @Input() public id: string;
  @Input() public element: any = null;
  @Input() public value: any = null;
  @Input() public disabled: boolean = false;
  @Input() public setDefaultFirst: boolean = false;
  @Input() public setFirstIfOnly: boolean = false;
  @Input() public idSelected: string | number | null = null;
  @Input() public identification: string = 'id';
  @Input() public filterObject: any = undefined;
  @Input() public procedure: string | null = null;
  @Input() public parameters: any = undefined;
  @Input() public programName: string | null = null;
  @Input() public optionsFilter: ((options: any[]) => any[]) | null = null;
  @Input() public displayWithProperty: string = "";
  @Input() public showDisplayWithProperty: boolean = false;
  @Input() public showErrorMessage: boolean = true;
  @Input() public displayFunction: ((value: any) => string) | null = null;
  @Input() public serverSearch: boolean = false;
  @Input() public minimumSearchLength: number = 0;
  @Input() public searchDebounceTime: number = 300;
  @Input() public searchParameter: string = 'search';
  @Input() public initialSearch: string | number | null = null;

  @Output() selectOption: EventEmitter<any> = new EventEmitter();
  @Output() clearOption: EventEmitter<any> = new EventEmitter();
  @Output() optionSelected: EventEmitter<any> = new EventEmitter();
  @Output() optionResolved: EventEmitter<any> = new EventEmitter();
  @Output() optionsLoaded: EventEmitter<any[]> = new EventEmitter();
  @Output() inputBlur: EventEmitter<any> = new EventEmitter();
  @Output() inputChange: EventEmitter<any> = new EventEmitter();
  @Output() inputKeyup: EventEmitter<any> = new EventEmitter();
  @Output() inputKeydown: EventEmitter<any> = new EventEmitter();
  constructor() { }

  public standaloneControl = new FormControl<any>(null);
  public filteredOptions$: Observable<any[]> = of([]);

  @ViewChild('autocompleteInput') inputRef: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private initialized = false;
  private loadVersion = 0;
  private loadedOptions: any[] = [];
  private lastLoadSignature = '';
  private readonly serverSearchTerms = new Subject<string>();
  private serverSearchSubscription?: Subscription;

  get displayWith() {
    return (value: any) => {

      if (!value) return '';

      if (this.displayFunction) {
        return this.displayFunction.call(this.originalClass ?? null, value) ?? '';
      }

      if (this.showDisplayWithProperty) {
        return value[this.displayWithProperty];
      }

      if (value['displayWith']) {
        return value['displayWith'];
      }

      return this.service?.modelField?.[this.formControlName]?.display(value)
        ?? this.service?.model?.display(value)
        ?? '';
    };
  }

  optionLabel(option: any): string {
    if (this.displayFunction) {
      return this.displayFunction.call(this.originalClass ?? null, option) ?? '';
    }
    if (this.displayWithProperty) {
      return option?.[this.displayWithProperty] ?? '';
    }
    if (option?.displayWith) {
      return option.displayWith;
    }
    return this.service?.modelField?.[this.formControlName]?.display(option) ?? String(option ?? '');
  }

  focusInput(): void {
    this.inputRef?.nativeElement?.focus();
    setTimeout(() => this.trigger?.openPanel(), 0);
  }

  clearInput(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.formGroup) this.formGroup.get(this.formControlName)?.setValue('');
    else this.standaloneControl.setValue('');
    this.focusInput();
    this.clearOption.emit();
  }

  ngOnInit(): void {
    this.initialized = true;
    if (!this.formGroup) {
      this.standaloneControl.setValue(this.value, { emitEvent: false });
      if (this.disabled) this.standaloneControl.disable({ emitEvent: false });
    }
    if (!this.service) {
      return;
    }
    if (!this.service.modelField[this.formControlName]) {
      this.service.modelField[this.formControlName] = this.service.model.convertToModel("");
    }
    if (this.serverSearch) {
      this.configureServerSearch();
      this.requestServerSearch(this.initialSearch);
    } else if (this.autoLoad) void this.loadOptions();
    else this.filteredOptions$ = this.service.modelFilteredList?.[this.formControlName] ?? of([]);
  }

  ngOnDestroy(): void {
    this.serverSearchSubscription?.unsubscribe();
  }

  handleInput(event: Event): void {
    this.inputChange.emit(event);
    if (!this.serverSearch) return;
    this.requestServerSearch((event.target as HTMLInputElement | null)?.value);
  }

  private configureServerSearch(): void {
    this.serverSearchSubscription?.unsubscribe();
    this.serverSearchSubscription = this.serverSearchTerms.pipe(
      debounceTime(this.searchDebounceTime),
      distinctUntilChanged(),
      switchMap((search) => from(this.loadServerSearchOptions(search))),
    ).subscribe((result) => {
      if (!result || result.loadVersion !== this.loadVersion) return;
      this.setLoadedOptions(result.options, true);
    });
  }

  private requestServerSearch(value: string | number | null | undefined): void {
    const search = String(value ?? '').trim();
    if (search.length < this.minimumSearchLength) {
      this.loadVersion++;
      this.loadedOptions = [];
      this.filteredOptions$ = of([]);
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.serverSearchTerms.next(search);
  }

  private async loadServerSearchOptions(search: string): Promise<{ loadVersion: number; options: any[] }> {
    const loadVersion = ++this.loadVersion;
    const registros = await this.service.customListAll(
      undefined,
      undefined,
      this.formControlName,
      this.procedure ?? 'pi-listar',
      { ...(this.parameters ?? {}), [this.searchParameter]: search },
      undefined,
      this.programName ?? undefined,
    );
    return {
      loadVersion,
      options: (registros ?? []).map((registro) => this.service.model.convertToModel(registro)),
    };
  }

  private async loadOptions(): Promise<void> {
    const signature = JSON.stringify({
      isCampoGenerico: this.isCampoGenerico,
      modulo: this.modulo,
      assunto: this.assunto,
      assuntos: this.assuntos,
      filterObject: this.filterObject,
      procedure: this.procedure,
      parameters: this.parameters,
      programName: this.programName,
      identification: this.identification,
    });
    if (signature === this.lastLoadSignature) return;
    this.lastLoadSignature = signature;
    const loadVersion = ++this.loadVersion;
    const genericoArgs = this.isCampoGenerico
      ? [this.modulo, this.assunto] as [string, string]
      : undefined;
    const registros = this.isCampoGenerico && this.assuntos?.length
      ? (await Promise.all(
        this.assuntos.map((assunto) => this.service.customListAll(
          undefined,
          undefined,
          this.formControlName,
          'pi-listar-modulo-assunto',
          { modulo: this.modulo, assunto }
        ))
      )).flat()
      : this.procedure || this.programName
      ? await this.service.customListAll(
        undefined,
        undefined,
        this.formControlName,
        this.procedure ?? undefined,
        this.parameters,
        undefined,
        this.programName ?? undefined
      )
      : await this.service.listAll(
        undefined,
        this.formGroup,
        this.formControlName,
        genericoArgs,
        undefined,
        this.identification,
        this.filterObject,
        this.setDefaultFirst,
        this.setFirstIfOnly,
        { idSelected: this.identification === 'id' ? this.idSelected : null }
      );
    if (loadVersion !== this.loadVersion) return;

    const options = (registros ?? []).map((registro) => this.service.model.convertToModel(registro));
    this.setLoadedOptions(options);
  }

  private setLoadedOptions(options: any[], serverFiltered: boolean = false): void {
    this.loadedOptions = options;
    const control = this.formGroup?.get(this.formControlName) ?? this.standaloneControl;
    const filterModel = this.service.modelField?.[this.formControlName] ?? this.service.model;

    this.filteredOptions$ = serverFiltered ? of(options) : control.valueChanges.pipe(
      startWith(control.value),
      map((value) => value == null ? '' : value),
      map((value) => {
        const availableOptions = this.optionsFilter
          ? this.optionsFilter.call(this.originalClass ?? null, options)
          : options;
        const displayWith = typeof value === 'object'
          ? (value as Record<string, unknown>)?.['displayWith']
          : undefined;
        const search = displayWith != null && displayWith !== '' ? String(displayWith) : String(value);
        if (!search) return availableOptions.slice();
        if (this.displayFunction) {
          const normalizedSearch = search.toLocaleLowerCase('pt-BR');
          return availableOptions.filter((option) =>
            String(this.displayFunction?.call(this.originalClass ?? null, option) ?? '')
              .toLocaleLowerCase('pt-BR')
              .includes(normalizedSearch)
          );
        }
        return filterModel?.filter
          ? filterModel.filter(search, availableOptions)
          : availableOptions.slice();
      })
    );
    this.selectById(options);
    this.optionsLoaded.emit(options);
    this.changeDetectorRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.formGroup && changes['value'] && !changes['value'].firstChange) {
      this.standaloneControl.setValue(changes['value'].currentValue, { emitEvent: false });
    }
    if (!this.formGroup && changes['disabled'] && !changes['disabled'].firstChange) {
      changes['disabled'].currentValue
        ? this.standaloneControl.disable({ emitEvent: false })
        : this.standaloneControl.enable({ emitEvent: false });
    }
    if (changes['idSelected'] && !changes['idSelected'].firstChange) {
      this.selectById(this.loadedOptions);
    }
    if (this.initialized && this.serverSearch && changes['initialSearch'] && !changes['initialSearch'].firstChange) {
      this.requestServerSearch(changes['initialSearch'].currentValue);
    }
    if (this.initialized && changes['serverSearch'] && !changes['serverSearch'].firstChange) {
      if (this.serverSearch) {
        this.configureServerSearch();
        this.requestServerSearch(this.initialSearch);
      } else {
        this.serverSearchSubscription?.unsubscribe();
      }
    }
    if (
      this.initialized &&
      !this.serverSearch &&
      this.autoLoad &&
      ['autoLoad', 'service', 'formGroup', 'formControlName', 'isCampoGenerico', 'modulo', 'assunto', 'assuntos', 'filterObject', 'procedure', 'parameters', 'programName']
        .some((inputName) => changes[inputName] && !changes[inputName].firstChange)
    ) {
      if (changes['service'] || changes['formGroup'] || changes['formControlName']) {
        this.lastLoadSignature = '';
      }
      void this.loadOptions();
    }
  }

  private selectById(options: any[]): void {
    if (this.idSelected === null || this.idSelected === undefined || this.idSelected === '') return;
    const selected = options.find((option) => option?.[this.identification] == this.idSelected);
    const control = this.formGroup?.get(this.formControlName) ?? this.standaloneControl;
    if (selected && control) {
      control.setValue(selected, { emitEvent: false });
      this.optionResolved.emit(selected);
    }
  }

  onSelectOption(event) {
    if (this.service && event.option.value) {
      this.service.modelField[this.formControlName] = this.service.model.convertToModel(event.option.value);
    }

    const selected = event.option.value;
    const selectedValue = this.service ? this.service.modelField[this.formControlName] : selected;
    if (this.formGroup) this.formGroup.controls[this.formControlName].setValue(selectedValue);
    else this.standaloneControl.setValue(selectedValue);
    this.optionSelected.emit(event);
    this.selectOption.emit({ selectedOption: selected, fieldType: this.formControlName });

    if (this.onSelectOptionAction) {
      this.onSelectOptionAction(event, this.formGroup, this.originalClass, this.element);
    }
  }

  writeValue(obj: any) { }
  registerOnChange(fn: any) { }
  registerOnTouched(fn: any) { }
  setDisabledState(isDisabled: boolean) { }
}
